/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { AddSharp, Search } from "@mui/icons-material";
import {
  Alert,
  Button,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  TextField,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Warehouse } from "../../../@types/DRO/GetAllWarehouseResponse";
import { GetAllWarehouseListApi } from "../../../api/GetWarehouseList/GetAllWarehouseListApi";
import { useLoading } from "../../../views/Loader/LoadingContext";
import SideBarNavigation from "../../Layouts/Sidebar/Sidebar";
import WarehouseTableComponent from "../../Layouts/Table/WarehouseTableComponent";

type EnabledFilter = "enabled" | "disabled";

const WarehouseListPage = () => {
  const { id } = useParams();
  const { setLoading } = useLoading();
  // "enabled" added so the table can render a status column/switch
  const columns = ["id", "name", "shortName", "companyName", "enabled"];
  const columnHeaders = [
    "Warehouse ID",
    "Warehouse Name",
    "Warehous Code",
    "Company Name",
    "Status",
  ];
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [enabledFilter, setEnabledFilter] = useState<EnabledFilter>("enabled");
  const [totalPages, setTotalPages] = useState(1);
  const [warehouseData, setWarehouseData] = useState<Warehouse[]>([]);

  // Per-row loading state while a status toggle request is in flight
  const [togglingIds, setTogglingIds] = useState<{ [id: number]: boolean }>({});

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  const fetchWarehouseData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await GetAllWarehouseListApi.api.getAllWarehouseList(
        page,
        rowsPerPage,
        debouncedSearchQuery,
        undefined,
        undefined,
        enabledFilter === "enabled"
      );
      setWarehouseData(response.data.data);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error("Error fetching warehouse data", error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearchQuery, enabledFilter]);

  useEffect(() => {
    fetchWarehouseData();
  }, [fetchWarehouseData]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim());
    }, 350);

    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const handleEnabledFilterChange = (e: SelectChangeEvent) => {
    setEnabledFilter(e.target.value as EnabledFilter);
    setPage(0);
  };

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  // Optimistically flips the row's `enabled` flag, calls the API, and
  // rolls back on failure.
  const handleToggleStatus = async (warehouseId: number) => {
    setTogglingIds((prev) => ({ ...prev, [warehouseId]: true }));

    const previous = warehouseData;
    setWarehouseData((prev) =>
      prev.map((w) =>
        w.id === warehouseId ? { ...w, enabled: !w.enabled } : w
      )
    );

    try {
      const response = await GetAllWarehouseListApi.api.changeWarehouseStatus(
        warehouseId
      );
      setSnackbarMessage(response.message || "Warehouse status updated");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error: any) {
      // Roll back optimistic update
      setWarehouseData(previous);
      setSnackbarMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to update warehouse status"
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setTogglingIds((prev) => ({ ...prev, [warehouseId]: false }));
    }
  };

  return (
    <div className="flex h-screen">
      <div className="w-1/6 bg-white shadow-md">
        <SideBarNavigation />
      </div>
      <div className="flex-1 p-6 ml-8 bg-[#F8FAFC]">
        <h1 className="text-3xl font-semibold mb-6 text-left ml-4">
          Warehouse Details
        </h1>
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-4">
            <TextField
              placeholder="Search by name or code"
              value={searchQuery}
              onChange={handleSearchChange}
              size="small"
              sx={{
                width: 250,
                bgcolor: "white",
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#e5e7eb",
                  },
                  "&:hover fieldset": {
                    borderColor: "#d1d5db",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#3b82f6",
                  },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Search sx={{ color: "#9ca3af" }} />
                  </InputAdornment>
                ),
              }}
            />
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 font-medium">Status :</span>
              <Select
                size="small"
                value={enabledFilter}
                onChange={handleEnabledFilterChange}
                sx={{ minWidth: 100, bgcolor: "white", fontSize: 14 }}>
                <MenuItem value="enabled">Active</MenuItem>
                <MenuItem value="disabled">Inactive</MenuItem>
              </Select>
            </div>
          </div>
          <Link to="/create/warehouse">
            <Button
              variant="contained"
              startIcon={<AddSharp />}
              sx={{
                textTransform: "none",
                bgcolor: "#171F21",
                "&:hover": { bgcolor: "#0077F" },
                fontSize: "16px",
                borderRadius: "30px",
                px: 3,
                py: 1,
              }}>
              Create Warehouse
            </Button>
          </Link>
        </div>
        <div>
          <WarehouseTableComponent
            columns={columns}
            columnHeaders={columnHeaders}
            data={warehouseData}
            setSelectedOrder={() => {}}
            currentPage={page}
            setCurrentPage={setPage}
            totalPages={totalPages}
            onToggleStatus={handleToggleStatus}
            togglingIds={togglingIds}
          />
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default WarehouseListPage;